import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Play } from 'lucide-react';
import ReactPlayer from 'react-player';

export const Hero = () => {
  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Full Background Image */}
      <div 
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_site-creator-909/artifacts/mc91xnl1_DJI_0102.JPG)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      />
      
      {/* Dark overlay for text readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70 z-0" />
      
      {/* Content */}
      <div className="container mx-auto max-w-5xl px-4 md:px-6 lg:px-8 relative z-10 text-center py-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="space-y-6"
        >
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-2xl md:text-3xl lg:text-4xl font-semibold tracking-wide text-white"
            data-testid="hero-brand"
          >
            Green Lens Collective
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight leading-[1.1] text-white"
            data-testid="hero-title"
          >
            Media & Education<br />for a Greener Future
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-xl md:text-2xl leading-relaxed text-white/90 max-w-4xl mx-auto"
            data-testid="hero-subtitle"
          >
            Green Lens Collective is an environmental media and education NGO
            working alongside communities and conservation partners across
            Europe. We give visibility to environmental work and support those
            committed to a healthier planet.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 justify-center pt-4 flex-wrap"
          >
            <button
              onClick={scrollToContact}
              className="rounded-full bg-[#2E5C44] text-white hover:bg-[#234734] px-10 py-6 text-lg font-medium shadow-2xl hover:shadow-3xl transition-all duration-300 flex items-center justify-center gap-2 group"
              data-testid="hero-cta-btn"
            >
              Get In Touch
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
            </button>
            <a
              href="https://www.youtube.com/watch?v=m18WvP6CyIc"
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-full bg-[#D97706] text-white hover:bg-[#B45309] px-10 py-6 text-lg font-medium shadow-2xl hover:shadow-3xl transition-all duration-300 flex items-center justify-center gap-2 group"
              data-testid="hero-video-btn"
            >
              <Play className="w-5 h-5" />
              Introduction Video
            </a>
            <a
              href="#about"
              onClick={(e) => {
                e.preventDefault();
                const element = document.querySelector('#about');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              className="rounded-full border-2 border-white text-white hover:bg-white hover:text-[#1A2D23] px-10 py-6 text-lg font-medium transition-all duration-300 text-center"
              data-testid="hero-learn-more-btn"
            >
              Learn More
            </a>
          </motion.div>

          {/* Scroll indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
          >
            <div className="flex flex-col items-center gap-2 text-white/60">
              <span className="text-sm">Scroll to explore</span>
              <motion.div
                animate={{ y: [0, 8, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <ArrowRight className="w-5 h-5 rotate-90" />
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;